/**
 * @file StrategiesList.jsx
 * @description Manages trading strategies display and selection
 *
 * @section Responsibilities
 * - Display trading strategies
 * - Handle strategy selection
 * - Manage strategy state
 * - Process strategy data
 * - Handle strategy errors
 * - Provide strategy controls
 *
 * @section Dependencies
 * - StrategyService: Strategy management
 * - DataService: Data processing
 * - StateService: State management
 * - ControlService: Control handling
 *
 * @section Integration Points
 * - Strategy API: Strategy operations
 * - Data System: Data processing
 * - State System: State management
 * - Control System: Strategy controls
 *
 * @section State Management
 * - strategyState: Strategy status
 * - selectionState: Selection status
 * - dataState: Data status
 * - errorState: Error handling
 *
 * @section Events
 * - onStrategySelect: Strategy selection
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Array} strategies - Available strategies
 * @prop {Function} onSelect - Selection callback
 * @prop {Object} initialSelection - Initial selection
 * @prop {Boolean} autoSelect - Controls auto-selection
 */

import React, { useState, useCallback, useEffect, PureComponent } from "react";
import Widget from "../Widget";
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ReferenceLine,
} from "recharts";
import DgButton from "../../../../components/DgButton";
import DgModal from "../../../../components/DgModal";
import TextInput from "../../../../components/forms/TextInput";
import Request from "../../../../utils/fetchUtils";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { formatAmount } from "../../../../utils/formatAmount";
import Loader from "../Loader";
import { COLORS } from "../../../../enums/pieChartColors";
import FilterSelect from "../../../../components/dashboard/FilterSelect";

class CustomizedLabel extends PureComponent {
  render() {
    const { x, y, stroke, value } = this.props;
    return (
      <text
        x={x}
        y={y}
        dy={-4}
        fill={"white"}
        fontSize={10}
        textAnchor="middle"
      >
        {value}
      </text>
    );
  }
}

const StrategiesList = () => {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [brokerId, setBrokerId] = useState("TRADIER");
  const [fee, setFee] = useState(0);
  const [selectedStrat, setselectedStrat] = useState();
  const [newStrategyModal, setNewStrategyModal] = useState(false);
  const [tradeDashModal, setTradeDashModal] = useState(false);
  const [successModal, setSuccesssModal] = useState(false);
  const [updateFeeModal, setupdateFeeModal] = useState(false);
  const role = localStorage.getItem("role");
  let data = [];

  const handleFetchData = async (queryFilters = "") => {
    const sess = JSON.parse(localStorage.session);
    const userId = sess?.userId;
    if (typeof userId === "string") {
      setLoading(true);
      const res = await Request(`/trading/strategy?q=&${queryFilters}`);
      if (res.ok) {
        setLoading(false);
        data = res.data;
        setStrategies(data);
      }
    }
  };

  const createStrategy = async (name) => {
    if (name !== "" && brokerId !== "") {
      setLoading(true);
      const res = await Request(
        `/trading/strategy`,
        {
          name,
          brokerId,
        },
        "POST"
      );
      setLoading(false);
      if (res.ok) {
        openSuccessModal();
        handleFetchData();
      }
    } else {
      Swal.fire(
        "Heads up!",
        "Please fill out all fields before continuing",
        "warning"
      );
    }
  };

  const updateStrategyFee = async () => {
    const res = await Request(
      `/trading/strategy/${selectedStrat.strategyId}`,
      {
        feePercentage: fee,
      },
      "PUT"
    );
    if (
      res.Attributes &&
      res.Attributes.feePercentage &&
      res.Attributes.feePercentage.S
    ) {
      const feeValue = res.Attributes.feePercentage.S;
      Swal.fire("Fee updated", "", "success");

      setStrategies((prevStrategies) =>
        prevStrategies.map((strategy) =>
          strategy.strategyId === selectedStrat.strategyId
            ? { ...strategy, feePercentage: feeValue }
            : strategy
        )
      );

      setFee(feeValue);
      setupdateFeeModal(false);
    }
  };

  const fetch = useCallback(() => {
    handleFetchData();
  }, []);

  useEffect(() => {
    fetch();
  }, []);

  const handleNewStrategyModal = (isOpen) => {
    setNewStrategyModal(isOpen);
  };

  const handleSuccessModal = (isOpen) => {
    setSuccesssModal(isOpen);
  };

  const handleTradeDashModal = (isOpen) => {
    setTradeDashModal(isOpen);
  };

  const openSuccessModal = () => {
    handleNewStrategyModal(false);
    handleSuccessModal(true);
  };

  const mapHistoricalToLineChart = (strategy = { historical: [] }) => {
    const data = strategy?.historical?.map(
      ({
        SK,
        balance: {
          balances: { total_equity },
        },
      }) => {
        const [partition, id, date] = SK.split("#");
        return {
          name: date,
          amount: total_equity,
        };
      }
    );

    const minEquity = Math.min(...data.map((d) => d.amount));
    const maxEquity = Math.max(...data.map((d) => d.amount));

    return { data, minEquity, maxEquity };
  };

  //TODO: USE LATER
  const mapPieChart = (strategy, checkPositive) => {
    return strategy.performance.positions
      .filter((p) => (checkPositive ? p.gainLoss > 0 : p.gainLoss < 0))
      .map((p) => ({
        name: p.symbol,
        value: parseFloat(Math.abs(p.gainLoss).toFixed(2)),
      }));
  };

  const mapPositions = (strategy) => {
    //TODO: REVIEW NEGATIVE VALUES
    const totalMarketValue = strategy.performance.positions.reduce(
      (acc, p) => acc + p.last * p.quantity,
      0
    );
    const percentageAssets = strategy.performance.positions.map((p) => {
      const marketValue = p.last * p.quantity;
      let percentage = (marketValue / totalMarketValue) * 100;
      return {
        name: p.symbol,
        value: parseFloat(percentage.toFixed(2)),
      };
    });
    const relevantAssets = percentageAssets.filter(
      (p) => Math.abs(p.value) >= 5
    );
    const irrevantAssetsCount = percentageAssets.filter(
      (p) => Math.abs(p.value) < 5
    );
    return [
      ...relevantAssets,
      {
        name: "OTHERS",
        value: irrevantAssetsCount.reduce((acc, p) => acc + p.value, 0),
      },
    ];
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${name}`}
      </text>
    );
  };

  return (
    <Widget name={"strategies"}>
      <FilterSelect onFilter={handleFetchData} />
      {!loading ? (
        <>
          <div className="heading">
            <h1 className="title">Strategies</h1>
            <div
              className="blank-btn"
              onClick={() => {
                handleNewStrategyModal(true);
              }}
            >
              <img src="/assets/icons/plus.svg" />
              <span>New strategy</span>
            </div>
          </div>
          <div className="mt-10">
            {strategies.map((strategy) => (
              <div className="overview-strategy " key={strategy.strategyId}>
                <div className="col-span-1 pt-5">
                  <div className="strategy-name">{strategy.name}</div>
                  <div className="bull-bigger">
                    <img src="/assets/icons/dashboard/bull-bigger.svg" />
                  </div>
                  <div className="total-amount small">
                    Total cash: $
                    {formatAmount(
                      strategy?.balance?.body?.balances?.total_cash,
                      2
                    )}
                  </div>
                  <DgButton
                    category="primary"
                    size="medium"
                    text="View strategy"
                    style={{ margin: "auto" }}
                    action={() => {
                      navigate(`/strategies/strategy/${strategy.strategyId}`);
                    }}
                  />
                  <br />
                  {role === "admin" && (
                    <div
                      className="total-amount small"
                      style={{ fontSize: "18px" }}
                    >
                      Fund specific fee: {strategy.feePercentage || "-"} %{" "}
                      <DgButton
                        category="secondary"
                        size="small"
                        text="Edit"
                        style={{ margin: "auto" }}
                        action={() => {
                          setupdateFeeModal(true);
                          setselectedStrat(strategy);
                          if (strategy.feePercentage) {
                            setFee(strategy.feePercentage);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="col-span-1">
                  <div className="performance">AUM:</div>
                  <div className="performance-amount">
                    ${formatAmount(strategy.performance.performance, 2)}
                  </div>
                  <div className="btn gradient">
                    {formatAmount(strategy.performance.performancePerc, 2)}%
                  </div>
                  <div className="performance-chart px-5 py-2">
                    {(() => {
                      const { data, minEquity, maxEquity } =
                        mapHistoricalToLineChart(strategy);
                      return (
                        <LineChart width={400} height={200} data={data}>
                          <Line
                            type="monotone"
                            dataKey="amount"
                            stroke="#3357FF"
                            strokeWidth={2}
                          />
                          <Tooltip />
                          <XAxis dataKey="name" height={60} />
                          <YAxis
                            type="number"
                            domain={[minEquity, maxEquity]}
                          />
                          <ReferenceLine
                            y={100000}
                            stroke="white"
                            strokeDasharray="3 3"
                          />
                        </LineChart>
                      );
                    })()}
                  </div>
                </div>
                <div className="col-span-1 pt-5">
                  <div style={{ width: "100%", height: 250 }}>
                    {(() => {
                      const data = mapPositions(strategy);
                      return (
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={data}
                              stroke="none"
                              cx={"50%"}
                              cy={"50%"}
                              innerRadius={40}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={renderCustomizedLabel}
                            >
                              {data.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            {/* <Pie
                          data={mapPieChart(strategy, true)}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          stroke="#A1BCA8"
                          paddingAngle={10}
                          outerRadius={60}
                          label={renderCustomizedLabel}
                          fill="#10b981"
                        />
                        <Pie
                          data={mapPieChart(strategy, false)}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          stroke="#A1BCA8"
                          paddingAngle={15}
                          innerRadius={70}
                          outerRadius={90}
                          fill="#ef4444"
                          label={renderCustomizedLabel}
                        /> */}
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="modal-new-strategy">
            <DgModal
              modalState={newStrategyModal}
              handleModalState={handleNewStrategyModal}
            >
              <h1 className="title">New Strategy</h1>
              <div className="strategy-content">
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Strategy name",
                      name: "strategyName",
                      tooltipBool: false,
                      value: name,
                      action: (e) => {
                        setName(e.target.value);
                      },
                    }}
                  />
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Risk score estimate",
                      name: "riskEstimate",
                      tooltipBool: true,
                      tooltipText: "Recommended risk score for this strategy",
                    }}
                  ></TextInput>
                </div>
                {/* <div className="form-field">
                  <SelectInput
                    customInput={{
                      label: "Brokers",
                      name: "brokerId",
                      value: brokerId,
                      action: (e) => {
                        setBrokerId(e.target.value);
                      },
                      tooltipBool: false,
                    }}
                    data={[{ value: "TRADIER", text: "Tradier" }]}
                  />
                </div> */}
              </div>
              <DgButton
                category="primary"
                size="large"
                text="Add strategy"
                disabled={loading}
                action={() => {
                  createStrategy(name);
                }}
              ></DgButton>
            </DgModal>
          </div>
          <div className="modal-new-strategy success">
            <DgModal
              modalState={successModal}
              handleModalState={handleSuccessModal}
            >
              <div className="strategy-content">
                <img src="/assets/illos/hand.png" />
                <h2>
                  Strategy has been <strong>successfully created</strong>.
                </h2>
              </div>
              <DgButton
                category="primary"
                size="large"
                text="Back to dashboard"
                action={() => {
                  handleSuccessModal(false);
                }}
              ></DgButton>
            </DgModal>
          </div>
          <DgModal
            modalState={updateFeeModal}
            handleModalState={setupdateFeeModal}
          >
            <h1 className="title">Update fee</h1>
            <div className="strategy-content">
              <div className="form-field">
                <TextInput
                  customInput={{
                    label: "Fee",
                    name: "strategyName",
                    tooltipBool: false,
                    type: "number",
                    value: fee,
                    action: (e) => {
                      setFee(e.target.value);
                    },
                  }}
                />
              </div>
            </div>
            <DgButton
              category="primary"
              size="large"
              text="Save fee"
              action={() => {
                updateStrategyFee();
              }}
            ></DgButton>
          </DgModal>
        </>
      ) : (
        <>
          <div className="heading">
            <h1 className="title">Strategies</h1>
          </div>
          <Loader></Loader>
        </>
      )}
    </Widget>
  );
};

export default StrategiesList;
